import { useEffect, useMemo, useState } from 'react';
import { UserGroupIcon } from '@heroicons/react/24/outline';
import {
  useAchievement,
  useBulkUpsertTargets,
  useTargetPOSPs,
  useTargets,
} from '@/hooks/useTargets';
import { useBrokerInsurers } from '@/hooks/useBrokerInsurers';
import { useInsuranceTypesLookup } from '@/hooks/useProducts';
import type { BulkTargetRow } from '@/services/targets';
import type { TargetMetric, TargetPeriodType } from '@/types';
import { Button, Card, EmptyState, LoadingSpinner } from '@/components/common';
import { cn } from '@/utils';
import { formatCurrency } from '@/utils/format';
import { TargetVsAchievementWidget } from './TargetVsAchievementWidget';

/**
 * Board #263 / #338 — the broker admin's performance targets matrix.
 *
 * Rows are the broker's POSPs and its non-POSP channels; columns are the
 * periods of the chosen period type (the twelve months of a calendar year, the
 * four financial quarters of a financial year, or the financial year itself;
 * the financial year runs April to March). The filter bar picks the slice of
 * the matrix: metric (premium or number of policies), and optionally one
 * insurer and one product line. "All" means the target that has no such
 * filter. Every edited cell is sent in one bulk upsert and the grid then
 * REFETCHES — it never repaints from what it hoped the server stored.
 *
 * "Apply to all POSPs" is a convenience only: the server writes one
 * INDIVIDUAL row per active POSP, so a later change to one POSP's number is an
 * ordinary edit.
 *
 * Achievement comes from the same endpoint the country analytics widget uses.
 * Cancelled, lapsed and expired policies never count towards a target.
 *
 * U-02 — compact layout: KPI row, a sticky-header/sticky-name grid, and
 * distinct loading / error / empty states. A failed POSP or target fetch is an
 * error, never "you have no POSPs" — and never an editable grid of blank cells
 * sitting over targets that are really stored.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const compactCurrency = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  notation: 'compact',
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

const countFormat = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

interface Column {
  /** First month of the period: what the API stores in `year` / `month`. */
  year: number;
  month: number;
  /** Short header text, e.g. "Mar", "Q1", "FY2026-27". */
  header: string;
  /** Used in cell aria-labels, e.g. "Mar 2026", "Q1 FY2026-27". */
  aria: string;
}

interface Entity {
  id: string;
  kind: 'posp' | 'channel';
  name: string;
  code: string;
}

function financialYearLabel(startYear: number): string {
  return `FY${startYear}-${String(startYear + 1).slice(-2)}`;
}

/** The financial year (start year) that contains today. */
function currentFinancialYear(now = new Date()): number {
  return now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
}

function buildColumns(periodType: TargetPeriodType, year: number): Column[] {
  if (periodType === 'month') {
    return MONTHS.map((m, i) => ({
      year,
      month: i + 1,
      header: m,
      aria: `${m} ${year}`,
    }));
  }
  const fy = financialYearLabel(year);
  if (periodType === 'quarter') {
    return [
      { year, month: 4, header: 'Q1', aria: `Q1 ${fy}` },
      { year, month: 7, header: 'Q2', aria: `Q2 ${fy}` },
      { year, month: 10, header: 'Q3', aria: `Q3 ${fy}` },
      { year: year + 1, month: 1, header: 'Q4', aria: `Q4 ${fy}` },
    ];
  }
  return [{ year, month: 4, header: fy, aria: fy }];
}

function cellKey(entityId: string, col: { year: number; month: number }): string {
  return `${entityId}:${col.year}:${col.month}`;
}

function percentText(percentage: number | null | undefined): string {
  return percentage === null || percentage === undefined ? '—' : `${percentage}%`;
}

/** Same thresholds as the widget's progress bar, so a cell and its row agree. */
function toneFor(percentage: number | null): string {
  if (percentage === null) return 'text-gray-400 dark:text-gray-500';
  if (percentage >= 100) return 'text-green-600 dark:text-green-400';
  if (percentage >= 60) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

function KpiTile({
  label,
  value,
  hint,
  loading,
  children,
}: {
  label: string;
  value: string;
  hint?: string;
  loading?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <Card padding="sm" className="min-w-0">
      <p className="truncate text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </p>
      {loading ? (
        <div
          data-testid="kpi-skeleton"
          className="mt-1.5 h-7 w-24 animate-pulse rounded bg-gray-200 dark:bg-gray-700"
        />
      ) : (
        <p className="mt-1 truncate text-xl font-semibold tabular-nums text-gray-900 dark:text-white">
          {value}
        </p>
      )}
      {children}
      {hint && !loading && (
        <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      )}
    </Card>
  );
}

const selectClass =
  'h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:focus:ring-primary-400';

export function TargetsPage() {
  const currentYear = new Date().getFullYear();
  const [periodType, setPeriodType] = useState<TargetPeriodType>('month');
  const [metric, setMetric] = useState<TargetMetric>('premium');
  const [insurerId, setInsurerId] = useState('');
  const [typeId, setTypeId] = useState('');
  // Calendar year in month mode; financial-year START year otherwise.
  const [year, setYear] = useState(currentYear);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [fanColumn, setFanColumn] = useState('0');
  const [fanAmount, setFanAmount] = useState('');

  const isMoney = metric === 'premium';
  const columns = useMemo(() => buildColumns(periodType, year), [periodType, year]);
  const periodTitle = periodType === 'month' ? String(year) : financialYearLabel(year);

  const pospsQuery = useTargetPOSPs();
  // The server filters by year only for months: a financial quarter or year
  // spans two calendar years, so those are matched on the client by column.
  const targetsQuery = useTargets({
    ...(periodType === 'month' ? { year } : {}),
    period_type: periodType,
    metric,
  });
  const first = columns[0];
  const last = columns[columns.length - 1];
  const lastMonthOfRange =
    periodType === 'month'
      ? { year, month: 12 }
      : periodType === 'quarter'
        ? { year: last.year, month: last.month }
        : { year: year + 1, month: 3 };
  const achievement = useAchievement({
    from: `${first.year}-${String(first.month).padStart(2, '0')}`,
    to: `${lastMonthOfRange.year}-${String(lastMonthOfRange.month).padStart(2, '0')}`,
    ...(periodType !== 'month' ? { period_type: periodType } : {}),
    ...(metric !== 'premium' ? { metric } : {}),
    ...(insurerId ? { insurer: insurerId } : {}),
    ...(typeId ? { insurance_type: typeId } : {}),
  });
  const insurersQuery = useBrokerInsurers();
  const typesQuery = useInsuranceTypesLookup();
  const bulkSave = useBulkUpsertTargets();

  const insurerOptions = Array.isArray(insurersQuery.data) ? insurersQuery.data : [];
  const typeOptions = Array.isArray(typesQuery.data) ? typesQuery.data : [];

  const posps = useMemo(() => pospsQuery.data ?? [], [pospsQuery.data]);
  const targets = useMemo(() => targetsQuery.data ?? [], [targetsQuery.data]);
  const gridLoading = pospsQuery.isLoading || targetsQuery.isLoading;
  const gridError = pospsQuery.isError || targetsQuery.isError;

  const entities: Entity[] = useMemo(() => {
    const list: Entity[] = posps.map((p) => ({
      id: p.id,
      kind: 'posp',
      name: p.user_name,
      code: p.posp_code,
    }));
    // Channels: every non-POSP channel the achievement endpoint lists, PLUS any
    // channel with a stored target. The stored targets load independently of
    // achievement, so a failed achievement call never hides a stored target.
    const seen = new Set<string>();
    (achievement.data?.rows ?? []).forEach((row) => {
      if (row.entity_type === 'channel') {
        seen.add(row.entity_id);
        list.push({ id: row.entity_id, kind: 'channel', name: row.name, code: row.code });
      }
    });
    targets.forEach((t) => {
      if (t.channel && !seen.has(t.channel)) {
        seen.add(t.channel);
        const label = t.channel_name ?? t.channel;
        list.push({ id: t.channel, kind: 'channel', name: label, code: label });
      }
    });
    return list;
  }, [posps, achievement.data, targets]);
  const kindById = useMemo(() => {
    const map: Record<string, 'posp' | 'channel'> = {};
    entities.forEach((e) => {
      map[e.id] = e.kind;
    });
    return map;
  }, [entities]);

  // Any change of slice is a different set of stored values; drop pending
  // edits rather than silently carrying them onto other cells.
  useEffect(() => {
    setEdits({});
  }, [year, periodType, metric, insurerId, typeId]);

  const changePeriodType = (next: TargetPeriodType) => {
    setPeriodType(next);
    setYear(next === 'month' ? currentYear : currentFinancialYear());
    setFanColumn('0');
  };

  const stored = useMemo(() => {
    const map: Record<string, string> = {};
    targets.forEach((t) => {
      if ((t.period_type ?? 'month') !== periodType) return;
      if ((t.metric ?? 'premium') !== metric) return;
      if ((t.insurer ?? '') !== insurerId) return;
      if ((t.insurance_type ?? '') !== typeId) return;
      const subject = t.posp ?? t.channel;
      if (subject) map[cellKey(subject, t)] = t.target_amount;
    });
    return map;
  }, [targets, periodType, metric, insurerId, typeId]);

  const valueFor = (entityId: string, col: Column): string => {
    const key = cellKey(entityId, col);
    return key in edits ? edits[key] : (stored[key] ?? '');
  };

  const extras = (): Partial<BulkTargetRow> => ({
    ...(periodType !== 'month' ? { period_type: periodType } : {}),
    ...(metric !== 'premium' ? { metric } : {}),
    ...(insurerId ? { insurer: insurerId } : {}),
    ...(typeId ? { insurance_type: typeId } : {}),
  });

  // Clearing a cell that HAD a target is a real edit — it must be written as
  // 0, not quietly dropped, or the grid would show an empty cell while the
  // server still held the old number.
  const dirtyKeys = useMemo(
    () =>
      Object.entries(edits)
        // An edit on an entity we cannot classify (e.g. a channel that vanished
        // from the list) is never sent: it would be misread as a POSP id.
        .filter(([key]) => kindById[key.split(':')[0]] !== undefined)
        .filter(([key, value]) => {
          const trimmed = value.trim();
          const current = stored[key] ?? '';
          if (trimmed === '') return current !== '';
          return Number(trimmed) !== Number(current || 0);
        })
        .map(([key]) => key),
    [edits, stored, kindById]
  );

  const dirtyRows: BulkTargetRow[] = useMemo(
    () =>
      dirtyKeys.map((key) => {
        const [entityId, rowYear, rowMonth] = key.split(':');
        return {
          [kindById[entityId] === 'channel' ? 'channel' : 'posp']: entityId, // kind is known: filtered above
          year: Number(rowYear),
          month: Number(rowMonth),
          target_amount: (Number(edits[key].trim() || 0) || 0).toFixed(2),
          ...extras(),
        } as BulkTargetRow;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dirtyKeys, edits, kindById, periodType, metric, insurerId, typeId]
  );
  const dirtySet = useMemo(() => new Set(dirtyKeys), [dirtyKeys]);

  const fmt = (n: number | string) => (isMoney ? formatCurrency(n) : countFormat.format(Number(n)));
  const fmtCompact = (n: number) => (isMoney ? compactCurrency.format(n) : countFormat.format(n));

  // Row, column and grand totals of what the grid currently shows (unsaved
  // edits included — the header says so when there are any). The grand total
  // and "targeted" count are POSPs only; channels are shown beside them.
  const totals = useMemo(() => {
    const byEntity: Record<string, number> = {};
    const byColumn = columns.map(() => 0);
    let pospSum = 0;
    let targeted = 0;
    entities.forEach((entity) => {
      let rowSum = 0;
      columns.forEach((col, i) => {
        const n = Number(valueFor(entity.id, col)) || 0;
        rowSum += n;
        byColumn[i] += n;
      });
      byEntity[entity.id] = rowSum;
      if (entity.kind === 'posp') {
        pospSum += rowSum;
        if (rowSum > 0) targeted += 1;
      }
    });
    return { byEntity, byColumn, posp: pospSum, targeted };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entities, columns, edits, stored]);
  const gridTotal = totals.byColumn.reduce((a, b) => a + b, 0);

  // Period-level achievement per row, read from the same envelope the table
  // below renders — so the figure under a cell and the row's total reconcile.
  const achievedCell = useMemo(() => {
    const map: Record<string, { achieved: number; percentage: number | null }> = {};
    (achievement.data?.rows ?? []).forEach((row) => {
      if (row.entity_type === 'broker') return;
      (row.series ?? []).forEach((point) => {
        map[cellKey(row.entity_id, point)] = {
          achieved: Number(point.achieved) || 0,
          percentage: point.percentage,
        };
      });
    });
    return map;
  }, [achievement.data]);

  const handleSave = () => {
    if (dirtyRows.length === 0) return;
    bulkSave.mutate(dirtyRows, { onSuccess: () => setEdits({}) });
  };

  const fanCol = columns[Number(fanColumn)] ?? columns[0];
  const fanValid = Number(fanAmount) > 0 && fanAmount.trim() !== '';
  const handleFanOut = () => {
    if (!fanValid) return;
    bulkSave.mutate(
      {
        rows: [
          {
            year: fanCol.year,
            month: fanCol.month,
            target_amount: Number(fanAmount).toFixed(2),
            ...extras(),
          } as BulkTargetRow,
        ],
        options: { fan_out: 'all_posps' },
      },
      { onSuccess: () => setFanAmount('') }
    );
  };

  const retryGrid = () => {
    if (pospsQuery.isError) void pospsQuery.refetch();
    if (targetsQuery.isError) void targetsQuery.refetch();
  };

  const yearOptions =
    periodType === 'month'
      ? [currentYear - 1, currentYear, currentYear + 1]
      : [currentFinancialYear() - 1, currentFinancialYear(), currentFinancialYear() + 1];
  const summary = achievement.data?.summary;
  const brokerTarget = Number(summary?.target ?? 0);
  const noun = isMoney ? 'premium' : 'policies';
  const sliceText = [
    insurerId ? (insurerOptions.find((i) => i.id === insurerId)?.name ?? 'one insurer') : null,
    typeId ? (typeOptions.find((t) => t.id === typeId)?.name ?? 'one product line') : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="space-y-4">
      {/* Header: title left, filters + save right; wraps under the title on phones. */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">POSP Targets</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Performance targets for your POSPs and channels, by month, quarter or year (April to
            March). Your broker-level target is set by your country admin.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {dirtyRows.length > 0 && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
              {dirtyRows.length === 1 ? '1 unsaved change' : `${dirtyRows.length} unsaved changes`}
            </span>
          )}
          <select
            aria-label="Period type"
            value={periodType}
            onChange={(e) => changePeriodType(e.target.value as TargetPeriodType)}
            className={selectClass}
          >
            <option value="month">Monthly</option>
            <option value="quarter">Quarterly</option>
            <option value="year">Yearly</option>
          </select>
          <select
            aria-label="Year"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className={selectClass}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {periodType === 'month' ? y : financialYearLabel(y)}
              </option>
            ))}
          </select>
          <select
            aria-label="Metric"
            value={metric}
            onChange={(e) => setMetric(e.target.value as TargetMetric)}
            className={selectClass}
          >
            <option value="premium">Premium (₹)</option>
            <option value="policy_count">Number of policies</option>
          </select>
          <select
            aria-label="Insurer"
            value={insurerId}
            onChange={(e) => setInsurerId(e.target.value)}
            className={selectClass}
          >
            <option value="">All insurers</option>
            {insurerOptions.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name}
              </option>
            ))}
          </select>
          <select
            aria-label="Product line"
            value={typeId}
            onChange={(e) => setTypeId(e.target.value)}
            className={selectClass}
          >
            <option value="">All product lines</option>
            {typeOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <Button
            type="button"
            size="sm"
            className="h-9"
            onClick={handleSave}
            disabled={dirtyRows.length === 0 || bulkSave.isPending}
          >
            {bulkSave.isPending ? 'Saving…' : 'Save targets'}
          </Button>
        </div>
      </div>

      {/* KPI row. Achievement tiles never show 0 for "could not load" or "no target". */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="targets-kpis">
        <KpiTile
          label={`POSP targets · ${periodTitle}`}
          value={gridError ? '—' : fmt(totals.posp)}
          loading={gridLoading}
          hint={
            gridError ? 'Could not load' : `${totals.targeted} of ${posps.length} POSPs targeted`
          }
        />
        <KpiTile
          label="Broker target"
          value={achievement.isError ? '—' : brokerTarget > 0 ? fmt(brokerTarget) : '—'}
          loading={achievement.isLoading}
          hint={
            achievement.isError
              ? 'Could not load'
              : brokerTarget > 0
                ? 'Set by your country admin'
                : 'Not set by your country admin'
          }
        />
        <KpiTile
          label="Broker achieved"
          value={achievement.isError || !summary ? '—' : fmt(summary.achieved)}
          loading={achievement.isLoading}
          hint={
            achievement.isError
              ? 'Could not load'
              : `${isMoney ? 'Premium' : 'Policies'} on issued, active and matured policies`
          }
        />
        <KpiTile
          label="Achievement"
          value={achievement.isError ? '—' : percentText(summary?.percentage)}
          loading={achievement.isLoading}
          hint={
            achievement.isError
              ? 'Could not load'
              : summary?.percentage === null || summary?.percentage === undefined
                ? 'No broker target to measure against'
                : 'Of the broker target'
          }
        >
          {!achievement.isLoading &&
            !achievement.isError &&
            summary?.percentage !== null &&
            summary?.percentage !== undefined && (
              <div
                role="progressbar"
                aria-label="Broker achievement"
                aria-valuenow={summary.percentage}
                aria-valuemin={0}
                aria-valuemax={100}
                className="mt-1.5 h-1.5 w-full rounded-full bg-gray-100 dark:bg-gray-700"
              >
                <div
                  className={cn(
                    'h-1.5 rounded-full',
                    summary.percentage >= 100
                      ? 'bg-green-500'
                      : summary.percentage >= 60
                        ? 'bg-amber-500'
                        : 'bg-red-500'
                  )}
                  style={{ width: `${Math.min(summary.percentage, 100)}%` }}
                />
              </div>
            )}
        </KpiTile>
      </div>

      <Card padding="none" className="overflow-hidden">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-gray-100 px-4 py-3 dark:border-gray-700">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
              {periodTitle} {periodType === 'month' ? 'monthly' : periodType === 'quarter' ? 'quarterly' : 'yearly'}{' '}
              targets{sliceText ? ` · ${sliceText}` : ''}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Enter a {noun} target per period. The small figure under a cell is {noun} achieved in
              that period. Cancelled, lapsed and expired policies never count.
            </p>
          </div>
          {!gridLoading && !gridError && (
            <span className="text-sm tabular-nums text-gray-600 dark:text-gray-300">
              Total:{' '}
              <span className="font-semibold text-gray-900 dark:text-white">{fmt(gridTotal)}</span>
            </span>
          )}
        </div>

        {!gridLoading && !gridError && achievement.isError && (
          <div
            role="alert"
            className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300"
          >
            Achievement could not be loaded, so channels without a stored target are not listed
            here. Stored targets are still shown.
          </div>
        )}

        {!gridLoading && !gridError && posps.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-4 py-2 text-sm dark:border-gray-700">
            <span className="font-medium text-gray-700 dark:text-gray-200">Apply to all POSPs</span>
            <select
              aria-label="Apply to period"
              value={fanColumn}
              onChange={(e) => setFanColumn(e.target.value)}
              className={selectClass}
            >
              {columns.map((c, i) => (
                <option key={c.aria} value={String(i)}>
                  {c.aria}
                </option>
              ))}
            </select>
            <input
              type="number"
              min={0}
              inputMode="decimal"
              aria-label="Target for every POSP"
              placeholder={isMoney ? 'Amount each' : 'Policies each'}
              value={fanAmount}
              onChange={(e) => setFanAmount(e.target.value)}
              className="h-9 w-32 rounded-lg border border-gray-300 bg-white px-2.5 text-right text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-9"
              onClick={handleFanOut}
              disabled={!fanValid || bulkSave.isPending || dirtyRows.length > 0}
            >
              Apply to all POSPs
            </Button>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Writes one individual target per active POSP, replacing their number for that period.
              POSPs added later get none until you apply again.
            </span>
          </div>
        )}

        {gridLoading ? (
          <div className="flex justify-center py-10">
            <LoadingSpinner />
          </div>
        ) : gridError ? (
          <div
            role="alert"
            className="m-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300"
          >
            <span>
              {pospsQuery.isError
                ? 'Your POSPs could not be loaded, so targets cannot be edited right now.'
                : `The stored ${periodTitle} targets could not be loaded, so they cannot be edited right now.`}
            </span>
            <Button type="button" size="sm" variant="outline" onClick={retryGrid}>
              Retry
            </Button>
          </div>
        ) : entities.length === 0 ? (
          <EmptyState
            className="py-10"
            icon={<UserGroupIcon />}
            title="No active POSPs"
            description="You have no active POSPs yet, so there is nothing to set targets for."
          />
        ) : (
          // Both axes scroll inside the card: the header row and the name
          // column stay pinned, so a long roster never pushes the page down.
          <div className="max-h-[min(65vh,36rem)] overflow-auto">
            <table
              className={cn(
                'w-full table-fixed border-separate border-spacing-0 text-sm',
                periodType === 'month' && 'min-w-[60rem]'
              )}
            >
              <colgroup>
                <col className="w-32 sm:w-44" />
                {columns.map((c) => (
                  <col key={c.aria} />
                ))}
                <col className="w-24" />
              </colgroup>
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="sticky left-0 top-0 z-20 border-b border-gray-200 bg-gray-50 px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400"
                  >
                    POSP / channel
                  </th>
                  {columns.map((c) => (
                    <th
                      key={c.aria}
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-200 bg-gray-50 px-1 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400"
                    >
                      {c.header}
                    </th>
                  ))}
                  <th
                    scope="col"
                    className="sticky top-0 z-10 border-b border-gray-200 bg-gray-50 px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400"
                  >
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {entities.map((entity) => (
                  <tr key={`${entity.kind}-${entity.id}`} className="group">
                    <th
                      scope="row"
                      className="sticky left-0 z-[5] border-b border-gray-100 bg-white px-3 py-1.5 text-left font-normal group-hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-800 dark:group-hover:bg-gray-700"
                    >
                      <span className="block truncate font-medium text-gray-900 dark:text-gray-100">
                        {entity.name}
                      </span>
                      <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                        ({entity.code}){entity.kind === 'channel' ? ' · channel' : ''}
                      </span>
                    </th>
                    {columns.map((col) => {
                      const key = cellKey(entity.id, col);
                      const ach = achievedCell[key];
                      return (
                        <td
                          key={col.aria}
                          className="border-b border-gray-100 px-0.5 py-1 align-top group-hover:bg-gray-50 dark:border-gray-800 dark:group-hover:bg-gray-700/40"
                        >
                          <input
                            type="number"
                            min={0}
                            step={isMoney ? '0.01' : '1'}
                            inputMode="decimal"
                            aria-label={`${entity.code} ${col.aria} target`}
                            value={valueFor(entity.id, col)}
                            onChange={(e) =>
                              setEdits((prev) => ({
                                ...prev,
                                [key]: e.target.value,
                              }))
                            }
                            data-dirty={dirtySet.has(key) || undefined}
                            className={cn(
                              'h-8 w-full min-w-0 rounded-md border px-1.5 text-right text-xs tabular-nums text-gray-900 placeholder-gray-300',
                              '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
                              'focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-100 dark:focus:ring-primary-400',
                              dirtySet.has(key)
                                ? 'border-amber-400 bg-amber-50 dark:border-amber-500 dark:bg-amber-900/20'
                                : 'border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-900'
                            )}
                          />
                          {ach && ach.achieved > 0 && (
                            <span
                              title={`Achieved ${fmt(ach.achieved)}${
                                ach.percentage === null ? '' : ` (${ach.percentage}%)`
                              }`}
                              className={cn(
                                'mt-0.5 block truncate text-right text-[10px] tabular-nums leading-tight',
                                toneFor(ach.percentage)
                              )}
                            >
                              {fmtCompact(ach.achieved)}
                            </span>
                          )}
                        </td>
                      );
                    })}
                    <td className="border-b border-gray-100 px-3 py-1.5 text-right align-top text-xs font-semibold tabular-nums text-gray-900 group-hover:bg-gray-50 dark:border-gray-800 dark:text-gray-100 dark:group-hover:bg-gray-700/40">
                      <span className="inline-block pt-2">
                        {totals.byEntity[entity.id] > 0 ? fmt(totals.byEntity[entity.id]) : '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th
                    scope="row"
                    className="sticky bottom-0 left-0 z-20 border-t border-gray-200 bg-gray-50 px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400"
                  >
                    Period total
                  </th>
                  {totals.byColumn.map((sum, i) => (
                    <td
                      key={columns[i].aria}
                      data-testid={`month-total-${i + 1}`}
                      className="sticky bottom-0 z-10 truncate border-t border-gray-200 bg-gray-50 px-1 py-2 text-right text-[11px] font-semibold tabular-nums text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300"
                    >
                      {sum > 0 ? fmtCompact(sum) : '—'}
                    </td>
                  ))}
                  <td className="sticky bottom-0 z-10 border-t border-gray-200 bg-gray-50 px-3 py-2 text-right text-xs font-semibold tabular-nums text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-white">
                    {fmt(gridTotal)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* U-03: target vs achievement is the second half of the SAME card,
            not a card of its own -- one surface for "what we aimed at" and
            "what we did", under one period. */}
        <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-700" data-testid="targets-achievement">
          <h2 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
            Target vs achievement ({periodTitle})
          </h2>
          {achievement.isLoading ? (
            <div className="flex justify-center py-10">
              <LoadingSpinner />
            </div>
          ) : achievement.isError ? (
            <div
              role="alert"
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300"
            >
              <span>Achievement could not be loaded.</span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => void achievement.refetch()}
              >
                Retry
              </Button>
            </div>
          ) : (
            <TargetVsAchievementWidget
              rows={achievement.data?.rows ?? []}
              top={achievement.data?.top ?? []}
              bottom={achievement.data?.bottom ?? []}
              entityLabel="Broker / POSP / channel"
              rankingsAside
            />
          )}
        </div>
      </Card>
    </div>
  );
}

export default TargetsPage;
